import React, { useEffect, useRef, useState } from 'react';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import { Badge, Button, Card, MenuItem, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, List, Slider, TextField, Tooltip, Typography } from "@mui/material";
import _ from "lodash";
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Div from "@jumbo/shared/Div";
import { Stack } from "@mui/system";
import { useLoadScript } from "@react-google-maps/api";
import ConfirmableItemList from "app/shared/widgets/ConfirmableItemList";
import CustomPagination from 'app/widgets/Shared/Pagination/CustomPagination';
import customerServices from 'app/services/customer-services';
import {Search, SearchIconWrapper, StyledInputBase} from "../../shared/SearchGlobal/style";
import SearchIcon from "@mui/icons-material/Search";
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
import MoreTimeOutlinedIcon from '@mui/icons-material/MoreTimeOutlined';
import LockClockOutlinedIcon from '@mui/icons-material/LockClockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import TrashIconButton from 'app/shared/widgets/TrashIconButton';
import LocationDialog from 'app/pages/customers/LocationDialog';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { esES } from '@mui/x-date-pickers/locales';
import { es } from 'date-fns/locale';
import { GOOGLE_MAPS_API_KEY } from 'app/utils/constants/paths';



// DrawingManager fue removido de Google Maps en la v3.65; el dibujo de la
// geocerca se implementa manualmente (click a click) más abajo.
const GOOGLE_MAPS_LIBS = ["visualization", "places"]

const CustomersIndex = () => {  
    const {authUser} = useJumboAuth();
    const [customersList, setCustomersList] = useState([])
    const [listLoading, setListLoading] = useState(false)
    const [customerLoading, setCustomerLoading] = useState(false)
    const [openLocDialog, setOpenLocDialog] = useState(false)
    const [openContDialog, setOpenContDialog] = useState(false)
    const [openScheduleDialog, setOpenScheduleDialog] = useState(false)
    // Dirección cuyo horario se está por eliminar (confirmación previa).
    const [confirmClearSchedule, setConfirmClearSchedule] = useState(null)
    const [addressSchedules, setAddressSchedules] = useState({})
    const [savingSchedules, setSavingSchedules] = useState(false)
    const [currentCustomer, setCurrentCustomer] = useState(null)
    const [currCustomerDetails, setCurrCustomerDetails] = useState(null)
    const [currCustomerRecipients, setCurrCustomerRecipients] = useState([])
    // Direcciones del cliente (cada una con su geocerca). Reemplaza la antigua
    // geocerca única por cliente.
    const [customerAddresses, setCustomerAddresses] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [confiabilityRange, setConfiabilityRange] = useState([0, 100])
    const [searchQuery, setSearchQuery] = useState("")
    // '' todos, 'con' / 'sin' destinatarios de correo (Customer.with_email).
    const [emailFilter, setEmailFilter] = useState("")
    const newRecipients = useRef('');
    
    const {isLoaded} = useLoadScript({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBS
    });

    useEffect(() => {
        if (authUser) {
            console.log(authUser.current_company);
        } else {
            console.log('authUser is not defined yet.');
        }
    }, [authUser]);

    useEffect(() => {
        if(listLoading){
            loadCustomers()
        }
    }, [listLoading])

    useEffect(() => {
        setListLoading(true)
    }, [])

    // Mantiene los horarios editables del modal en sync con las direcciones.
    useEffect(() => {
        const map = {}
        customerAddresses.forEach((a) => {
            map[a.id] = {
                start: a.preferred_arrival_start || null,
                end: a.preferred_arrival_end || null,
            }
        })
        setAddressSchedules(map)
    }, [customerAddresses])

    const buildCustomersParams = () => {
        let par = []
        let pos = 0
        par.push(encodeURI(`q[m]=and`))
        if(searchQuery && searchQuery !== 0) {
            par.push(encodeURI(`q[g][${pos}][name_or_code_cont]=${searchQuery}`))
            pos += 1
        }
        if(confiabilityRange && confiabilityRange.length === 2) {
            // par.push(`q%5B1%5D%5Blocation_query_score_not_lt%5D=${confiabilityRange[0]/100.0}`)
            // par.push(`q%5B1%5D%5Blocation_query_score_not_lt%5D=${confiabilityRange[0]/100.0}`)
            let pos_i = 0
            par.push(encodeURI(`q[g][${pos}][m]=or`))
            if(confiabilityRange[0] <= 0){
                par.push(encodeURI(`q[g][${pos}][g][${pos_i}][location_query_score_null]=1`))
                pos_i += 1
            }
            par.push(encodeURI(`q[g][${pos}][g][${pos_i}][m]=and`))
            par.push(encodeURI(`q[g][${pos}][g][${pos_i}][location_query_score_gteq]=${confiabilityRange[0]/100.0}`))
            par.push(encodeURI(`q[g][${pos}][g][${pos_i}][location_query_score_lteq]=${confiabilityRange[1]/100.0}`))
            pos += 1
        }

        if (emailFilter) {
            par.push(encodeURI(`q[with_email]=${emailFilter}`))
        }

        par.push(`page=${currentPage}`)
        par.push(`include=location,recipients`)

        par = _.join(par, '&')

        return `?${par}`;
    }


    const handleSearchQueryChange = (newVal) => {
        setCurrentPage(1)
        setSearchQuery(newVal)
        setListLoading(true)
    }


    const debounceSearchQueryChange = _.debounce(handleSearchQueryChange, 450)
    
    const loadCustomers = () => {
        customerServices.getCustomers(buildCustomersParams())
            .then(data => {
                setCustomersList(data.data)
                setTotalPages(data.meta.total_pages)
            })
            .catch(function (error) {})
            .finally(() => {
                setListLoading(false)
            });
    }

    const getCustomerDetails = (customer) => {
        if(currentCustomer?.id != customer?.id) {
            setCurrCustomerDetails(null)
            setCurrCustomerRecipients([])
            setCustomerLoading(true)

            customerServices.getCustomer(customer?.id, "include=location,recipients")
                .then(response => {
                    setCurrCustomerDetails(response)
                    setCurrCustomerRecipients(_.map(response.recipients, 'email'))
                    setCustomerAddresses(response.addresses || [])
                })
                .catch(function (error) {})
                .finally(() => {
                    setCustomerLoading(false)
                });
        }
    }

    const saveCustomerRecipients = (customer, recipients) => {
        setCustomerLoading(true)
        customerServices.saveRecipients(customer.id, { recipients: recipients, include: "location,recipients" })
            .then(response => {
                setCurrCustomerDetails(response)
                setCurrCustomerRecipients(_.map(response.recipients, 'email'))
                loadCustomers()
            })
            .catch(function (error) {})
            .finally(() => {
                setCustomerLoading(false)
            });
    }

    const applyCustomerResponse = (response) => {
        setCurrCustomerDetails(response)
        setCustomerAddresses(response?.addresses || [])
        loadCustomers()
    }


    // "HH:MM" -> Date (para el TimePicker) y viceversa.
    const timeStringToDate = (timeStr) => {
        if (!timeStr) return null
        const d = new Date()
        d.setHours(parseInt(timeStr.substr(0, timeStr.indexOf(':')), 10))
        d.setMinutes(parseInt(timeStr.substr(timeStr.indexOf(':') + 1), 10))
        d.setSeconds(0)
        return d
    }

    const dateToTimeString = (date) => {
        if (!date) return null
        return `${_.padStart(date.getHours(), 2, '0')}:${_.padStart(date.getMinutes(), 2, '0')}`
    }

    const handleScheduleFieldChange = (addressId, field, value) => {
        setAddressSchedules((prev) => ({
            ...prev,
            [addressId]: { ...prev[addressId], [field]: dateToTimeString(value) },
        }))
    }

    // Deja la dirección sin horario preferente. Se aplica al guardar el modal,
    // igual que cualquier otro cambio del formulario.
    const hasSchedule = (addressId) => Boolean(addressSchedules[addressId]?.start || addressSchedules[addressId]?.end)

    // Deja la dirección sin horario preferente. Se aplica al guardar el modal,
    // igual que cualquier otro cambio del formulario.
    const clearAddressSchedule = (addressId) => {
        setAddressSchedules((prev) => ({
            ...prev,
            [addressId]: { start: null, end: null },
        }))
    }

    const saveAddressSchedules = () => {
        if (!currentCustomer) return
        const schedules = _.map(customerAddresses, (a) => ({
            address_id: a.id,
            preferred_arrival_start: addressSchedules[a.id]?.start || null,
            preferred_arrival_end: addressSchedules[a.id]?.end || null,
        }))
        setSavingSchedules(true)
        customerServices.saveAddressSchedules(currentCustomer.id, schedules)
            .then((response) => {
                applyCustomerResponse(response)
                setOpenScheduleDialog(false)
            })
            .catch(function (error) {})
            .finally(() => setSavingSchedules(false))
    }


    const renderRecipientsList = () => {
        if(currCustomerRecipients?.length <= 0) {
            return <Typography>No hay registros</Typography>
        } else {
            return (
                <React.Fragment>
                    <List dense={true}>
                        {_.map(currCustomerRecipients, (recipient) => {
                            return <ConfirmableItemList key={recipient} item={recipient} itemLabel="el destinatario" onTrash={() => removeRecipients(recipient) } />
                        })}
                    </List>
                </React.Fragment>
            )
        }
    }

    const addRecipients = () => {
        setCurrCustomerRecipients(_.union(currCustomerRecipients, _.split(newRecipients.current.value, ';')))
        newRecipients.current.value = []
    }

    const removeRecipients = (el) => {
        setCurrCustomerRecipients(_.without(currCustomerRecipients, el))
    }

    const renderRecipientsDialog = () => {
        return(
            <Dialog
                fullWidth={true}
                maxWidth={"sm"}
                open={openContDialog}
                onClose={() => { setOpenContDialog(false); setCurrCustomerDetails(null); setCurrCustomerRecipients([]); }}>
                <DialogTitle>
                    <Stack
                        direction="column"
                        justifyContent="start"
                        alignItems="left"
                        spacing={0}>
                        <Typography variant={"overline"} color="primary">{currentCustomer?.code}</Typography>
                        <Typography variant={"button"} color="primary">{currentCustomer?.name}</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Seleccione los usuarios que desea que reciban notificaciones relacionadas a este cliente. Para agregar múltiples destinatarios, ingrese los correos separados con punto y coma (';'). Ej: "correo1@dominio.cl; correo2@dominio.cl".
                    </DialogContentText>
                    <Grid item xs={12}>
                        <Typography sx={{mt: 4, mb: 2}} variant="h5" component="div">
                            Recipientes
                        </Typography>
                        <Div sx={{display: customerLoading ? 'block' : 'none', textAlign: 'center', my: 5}}>
                            <CircularProgress color="warning"/>
                        </Div>
                        {currCustomerRecipients && renderRecipientsList()}
                        <Stack
                            direction="row"
                            justifyContent="start"
                            alignItems="bottom"
                            spacing={0}>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="name"
                                label="Agregar nuevo(s) correo(s)"
                                type="email"
                                fullWidth
                                variant="standard"
                                inputRef={newRecipients}
                                sx={{mt: 2}} />
                            <Button onClick={() => addRecipients() }>Agregar</Button>
                        </Stack>
                    </Grid>

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenContDialog(false)}>Cerrar</Button>
                    <Button onClick={() => saveCustomerRecipients(currentCustomer, currCustomerRecipients)}>Guardar</Button>
                </DialogActions>
            </Dialog>
        )

    }

    const renderScheduleDialog = () => {
        return (
            <Dialog
                fullWidth={true}
                maxWidth={"md"}
                open={openScheduleDialog}
                onClose={() => { if (savingSchedules) return; setOpenScheduleDialog(false); }}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Stack direction="column" justifyContent="start" alignItems="left" spacing={0}>
                            <Typography variant={"overline"} color="primary">{currentCustomer?.code}</Typography>
                            <Typography variant={"button"} color="primary">{currentCustomer?.name}</Typography>
                            <Typography variant={"h4"} color="primary" sx={{ mt: 1 }}>Horario preferente por dirección</Typography>
                        </Stack>
                        <Tooltip title="El conductor verá este horario en la aplicación. Configúralo solo si es obligatorio.">
                            <InfoOutlinedIcon color="action" sx={{ cursor: 'help', flexShrink: 0 }} />
                        </Tooltip>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Define un rango horario recomendado de entrega (desde / hasta) para cada dirección del cliente.
                    </DialogContentText>
                    {customerAddresses.length === 0 &&
                        <Typography>Este cliente no tiene direcciones.</Typography>
                    }
                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={es}
                        localeText={{
                            ...esES.components.MuiLocalizationProvider.defaultProps.localeText,
                            okButtonLabel: 'Aceptar',
                        }}
                    >
                        {_.map(customerAddresses, (a, idx) => {
                            // La dirección sola alcanza para reconocer la fila. "Dirección N"
                            // queda de respaldo para las que se guardaron sin calle y tienen
                            // short_address en blanco, que si no dejarían la fila sin nombre.
                            const label = a.short_address?.trim() || `Dirección ${idx + 1}`
                            return (
                            <Stack key={`sched-${a.id}`} direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                                    {label}
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
                                    <TimePicker
                                        label="Desde"
                                        ampm={false}
                                        slotProps={{ textField: { size: 'small', sx: { width: 120 } }, actionBar: { actions: ['clear', 'accept'] } }}
                                        value={timeStringToDate(addressSchedules[a.id]?.start)}
                                        onChange={(v) => handleScheduleFieldChange(a.id, 'start', v)}
                                        renderInput={(params) => <TextField {...params} size="small" sx={{ width: 120 }} />}
                                    />
                                    <TimePicker
                                        label="Hasta"
                                        ampm={false}
                                        slotProps={{ textField: { size: 'small', sx: { width: 120 } }, actionBar: { actions: ['clear', 'accept'] } }}
                                        value={timeStringToDate(addressSchedules[a.id]?.end)}
                                        onChange={(v) => handleScheduleFieldChange(a.id, 'end', v)}
                                        renderInput={(params) => <TextField {...params} size="small" sx={{ width: 120 }} />}
                                    />
                                    <TrashIconButton
                                        title={hasSchedule(a.id) ? 'Eliminar el horario de esta dirección' : 'Esta dirección no tiene horario'}
                                        disabled={savingSchedules || !hasSchedule(a.id)}
                                        onClick={() => setConfirmClearSchedule({ id: a.id, label })}
                                    />
                                </Stack>
                            </Stack>
                            )
                        })}
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { if (savingSchedules) return; setOpenScheduleDialog(false); }}>Cerrar</Button>
                    <Button variant="contained" disabled={savingSchedules || customerAddresses.length === 0} onClick={saveAddressSchedules}>
                        {savingSchedules ? 'Guardando…' : 'Guardar'}
                    </Button>
                </DialogActions>

                <ConfirmDialog
                    open={Boolean(confirmClearSchedule)}
                    title="Eliminar horario de la dirección"
                    message={
                        <>
                            ¿Estás seguro que deseas eliminar el horario preferente de <strong>{confirmClearSchedule?.label}</strong>?
                            <br />
                            Las entregas a esta dirección dejarán de heredar ese horario.
                        </>
                    }
                    onClose={() => setConfirmClearSchedule(null)}
                    onConfirm={() => {
                        clearAddressSchedule(confirmClearSchedule.id);
                        setConfirmClearSchedule(null);
                    }}
                />
            </Dialog>
        )
    }

    const renderCustomersList = (user) => {
        console.log('user:', authUser?.current_company?.id);
        if(customersList){
            return (
                <React.Fragment>
                    <LocationDialog
                        open={openLocDialog}
                        onClose={() => setOpenLocDialog(false)}
                        customer={currentCustomer}
                        isLoaded={isLoaded}
                        onChanged={applyCustomerResponse}
                    />
                    {renderRecipientsDialog()}
                    {renderScheduleDialog()}
                    <List disablePadding>
                        {_.map(customersList, (c, i) => {
                            return (
                                <Card sx={{mb: 2}} key={c.id}>
                                    <CardContent sx={{display: 'flex'}}>
                                        <Grid container spacing={5}>
                                            <Grid item xs={12} md={5}>
                                            <Stack
                                                direction="column"
                                                justifyContent="flex-start"
                                                alignItems="flex-start"  // Changed 'left' to 'flex-start' for better alignment
                                                spacing={0.2}
                                                sx={{ height: '100%', minHeight: 90 }}>
                                                <Stack direction="column" alignItems="flex-start" spacing={0.2}>
                                                    {c.payment_code ? (
                                                        <Typography variant={"overline"} color="primary">{c.payment_code}</Typography>
                                                    ) : (
                                                        <Typography variant={"overline"} color="primary">{c.code}</Typography>
                                                    )}
                                                    <Typography variant={"button"} color="primary">{c.name}</Typography>
                                                    {c.alias && (
                                                        <Typography variant={"overline"} color="primary">{c.alias}</Typography>
                                                    )}
                                                </Stack>

                                                <Stack
                                                    direction="row"
                                                    justifyContent="start"
                                                    alignItems="center"
                                                    spacing={0.5}>
                                                        <Typography>
                                                            Nº Desp: {c.deliveries_count}
                                                        </Typography>
                                                        <Tooltip title="Editar dirección y geocerca">
                                                            <IconButton color={c.location?.path ? 'success' : 'error'} onClick={() => { setCurrentCustomer(c); getCustomerDetails(c); setOpenLocDialog(true); }}>
                                                                <Badge badgeContent={c.addresses?.length || 0} color="primary" showZero max={9} sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}>
                                                                    <PlaceOutlinedIcon />
                                                                </Badge>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Configurar horario preferente por dirección">
                                                            <IconButton onClick={() => { setCurrentCustomer(c); getCustomerDetails(c); setOpenScheduleDialog(true); }}>
                                                                <MoreTimeOutlinedIcon color="action" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Editar correos de notificación">
                                                            <IconButton onClick={() => { setCurrentCustomer(c); getCustomerDetails(c); setOpenContDialog(true); }}>
                                                                <Badge badgeContent={c.recipients?.length || 0} color="primary" showZero max={9} sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}>
                                                                    <EmailOutlinedIcon color="action" />
                                                                </Badge>
                                                            </IconButton>
                                                        </Tooltip>
                                                </Stack>

                                            </Stack>

                                            </Grid>
                                            <Grid item xs={12} md={7}>
                                                <Stack
                                                    direction="column"
                                                    justifyContent="start"
                                                    alignItems="stretch"
                                                    spacing={0.5}
                                                    sx={{ pl: { md: 4 } }}>

                                                    {(() => {
                                                        const addrs = (c.addresses && c.addresses.length)
                                                            ? c.addresses
                                                            : (c.location ? [{ id: 'loc', short_address: c.location.short_address, short_zone: c.location.short_zone, primary: true }] : [])
                                                        const multiple = addrs.length > 1
                                                        return (
                                                            <>
                                                                <Typography variant={"overline"}>
                                                                    Ubicación {multiple
                                                                        ? `(${addrs.length} direcciones)`
                                                                        : (c.location?.query_score ? `(${_.round(c.location.query_score * 100)}%)` : '')}
                                                                </Typography>
                                                                {addrs.length === 0 &&
                                                                    <Typography variant={"body2"}>Sin ubicación</Typography>
                                                                }
                                                                {_.map(addrs, (a, i) => (
                                                                    <Stack key={a.id ?? i} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                                        <Typography variant={"body2"}>
                                                                            {[a.short_address, a.short_zone].filter(Boolean).join(' · ')}
                                                                        </Typography>
                                                                        {(a.preferred_arrival_start || a.preferred_arrival_end) &&
                                                                            <Tooltip title="Horario preferente configurado para esta dirección">
                                                                                <Chip
                                                                                    size="small"
                                                                                    color="success"
                                                                                    variant="outlined"
                                                                                    icon={<LockClockOutlinedIcon fontSize="small" />}
                                                                                    label={`${a.preferred_arrival_start || ''}-${a.preferred_arrival_end || ''}`}
                                                                                    sx={{ flexShrink: 0 }}
                                                                                />
                                                                            </Tooltip>
                                                                        }
                                                                    </Stack>
                                                                ))}
                                                            </>
                                                        )
                                                    })()}
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </List>
                </React.Fragment>
            )
        } else {
            return(
                <Typography>
                    No se encontraron datos
                </Typography>
            )
        }
    }

    const confiabilityText = (value) => {
        return `${value}%`;
    };

// Añadir nuevos estados
const [openCreateDialog, setOpenCreateDialog] = useState(false);
const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    code: '',
    location: '',
    alias: '',  // Aquí asegúrate de incluir el alias
    // Otros campos que necesites para el nuevo cliente
});
const [loading, setLoading] = useState(false); // Nuevo estado para manejar la carga

// Función para manejar la apertura del diálogo
const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
};

// Función para manejar el cierre del diálogo
const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setNewCustomerData({
        name: '',
        code: '',
        location: '',
        // Resetea otros campos si es necesario
    });
    setLoading(false); // Resetea el estado de carga al cerrar el diálogo
};

// Función para guardar el nuevo cliente
const handleSaveNewCustomer = () => {
    setLoading(true); // Mostrar spinner

    customerServices.createCustomer(newCustomerData)
        .then(() => {
            loadCustomers(); // Recargar la lista de clientes después de crear uno nuevo
            handleCloseCreateDialog();
        })
        .catch(error => {
            console.error("Error al crear el cliente:", error);
        })
        .finally(() => {
            setLoading(false); // Ocultar spinner después de la creación
        });
};

// Agrega el diálogo de creación
const renderCreateCustomerDialog = () => {
    return (
        <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog}>
            <DialogTitle>Crear Nuevo Cliente</DialogTitle>
            <DialogContent>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <div>
                        <TextField
                            margin="dense"
                            label="Código"
                            fullWidth
                            value={newCustomerData.code}
                            onChange={(e) => setNewCustomerData({ ...newCustomerData, code: e.target.value })}
                        />
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Nombre"
                            fullWidth
                            value={newCustomerData.name}
                            onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                        />
                        <TextField
                            margin="dense"
                            label="Centro"  // Aquí el campo para capturar el "alias"
                            fullWidth
                            value={newCustomerData.alias}  // Asegúrate de agregar alias en el estado
                            onChange={(e) => setNewCustomerData({ ...newCustomerData, alias: e.target.value })}
                        />
                        {/* <TextField
                            margin="dense"
                            label="Ubicación"
                            fullWidth
                            value={newCustomerData.location}
                            onChange={(e) => setNewCustomerData({ ...newCustomerData, location: e.target.value })}
                        /> */}
                        {/* Añadir más campos si es necesario */}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseCreateDialog} disabled={loading}>Cancelar</Button>
                <Button onClick={handleSaveNewCustomer} color="primary" disabled={loading}>Guardar</Button>
            </DialogActions>
        </Dialog>
    );
};

return (
        <div>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={3}
                sx={{ mb: 2 }}>
                <Typography variant="h2">Clientes</Typography>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={3}>
                    
                    {authUser?.current_company?.id === 80 && (
                        <Button variant="contained" size="small" color="success" sx={{ px: 2, whiteSpace: 'nowrap' }} startIcon={<ControlPointOutlinedIcon />} onClick={handleOpenCreateDialog}>
                            Crear
                        </Button>
                    )}

                    
                    <Div sx={{ width: 300, maxWidth: 300 }}>
                        <Typography id="confiability-slider" sx={{ mb: 0 }} >
                            Confiabilidad ubicación
                        </Typography>
                        <Slider
                            size="small"
                            aria-labelledby="confiability-slider"
                            value={confiabilityRange}
                            onChange={(ev, val) => { setConfiabilityRange(val) }}
                            onChangeCommitted={(ev, val) => { setCurrentPage(1); setConfiabilityRange(val); setListLoading(true); }}
                            getAriaValueText={confiabilityText}
                            step={1}
                            marks={_.map([0, 20, 40, 60, 80, 100], (v) => { return { value: v, label: confiabilityText(v) } })}
                            valueLabelDisplay="auto"
                        />
                    </Div>
                    
                    <TextField
                        select
                        size="small"
                        label="Correo"
                        value={emailFilter}
                        onChange={(e) => { setEmailFilter(e.target.value); setCurrentPage(1); setListLoading(true); }}
                        sx={{ width: 170, background: 'white' }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="con">Con correo</MenuItem>
                        <MenuItem value="sin">Sin correo</MenuItem>
                    </TextField>

                    <Search sx={{ maxWidth: { sm: 200, md: 350 } }}>
                        <SearchIconWrapper>
                            <SearchIcon />
                        </SearchIconWrapper>

                        <StyledInputBase
                            placeholder="Buscar"
                            inputProps={{ 'aria-label': 'search' }}
                            sx={{ background: 'white' }}
                            onChange={(e) => {
                                debounceSearchQueryChange(e.target.value)
                            }}
                            onKeyDown={(e) => {
                                if (e.key == 'Enter') {
                                    handleSearchQueryChange(e.target.value)
                                }
                            }}
                        />
                    </Search>
                </Stack>
            </Stack>

            {renderCreateCustomerDialog()}

            <Div sx={{ display: listLoading ? 'block' : 'none', textAlign: 'center', my: 5 }}>
                <CircularProgress color="warning" />
            </Div>
            <Grid container spacing={3.75}>
                <Grid item xs={12}>
                    {renderCustomersList()}
                </Grid>
            </Grid>
            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageSelect={(page) => { setCurrentPage(page); setListLoading(true) }} />
        </div>
    );

};

export default CustomersIndex;